import Dagre, { Label } from "@dagrejs/dagre";
import { Edge, Node } from "@xyflow/react";
import ELK from "elkjs/lib/elk.bundled.js";

export type LayoutDirection = "TB" | "LR";

const DEFAULT_GROUP_PADDING = 100;
const DEFAULT_GROUP_SPACING = 200;
const elk = new ELK();

export const getLayoutedElements = async (
  nodes: Node[],
  edges: Edge[],
  options: { direction: LayoutDirection }
): Promise<{ nodes: Node[]; edges: Edge[] }> => {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: options.direction,
    nodesep: 75, // Distance between nodes in the same level
    ranksep: 250, // Distance between tree levels
  });

  edges.forEach((edge: Edge) => {
    return g.setEdge(edge.source, edge.target);
  });

  // First pass: set up all nodes with their measured dimensions
  const nonGroupNodes = nodes.filter((node) => node.type !== "group");
  nonGroupNodes.forEach((node: Label) => {
    return g.setNode(node.id, {
      ...node,
      width: node.measured?.width ?? 0,
      height: node.measured?.height ?? 0,
    });
  });

  Dagre.layout(g);

  // Calculate group node sizes based on their children
  const groupNodes: any[] = nodes
    .filter((node) => node.type === "group")
    // We reverse the order to work on groups left to right.
    .reverse();
  groupNodes.forEach((groupNode: Label) => {
    const childNodes = nodes.filter((node) => node.parentId === groupNode.id);

    if (childNodes.length === 0) {
      // If no children, use default size
      groupNode.width = 200;
      groupNode.height = 200;
      groupNode.position = { x: 0, y: 0 };
      groupNode.x = 0;
      groupNode.y = 0;
      g.setNode(groupNode.id, {
        ...groupNode,
      });
    } else {
      // Calculate bounding box for all children that belong to this group
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      childNodes.forEach((childNode: Label) => {
        const childWithPosition = g.node(childNode.id);
        if (!childWithPosition) return;
        const childWidth = childNode.measured?.width ?? 0;
        const childHeight = childNode.measured?.height ?? 0;
        const childX = childWithPosition.x ?? 0;
        const childY = childWithPosition.y ?? 0;

        minX = Math.min(minX, childX);
        maxX = Math.max(maxX, childX + childWidth);
        minY = Math.min(minY, childY);
        maxY = Math.max(maxY, childY + childHeight);
      });

      // Add padding around the group
      const padding = DEFAULT_GROUP_PADDING;
      const finalWidth = maxX - minX + padding;
      const finalHeight = maxY - minY + padding;

      // Calculate the center of the group based on children
      const groupX = minX - padding / 2;
      const groupY = minY - padding / 2;

      // Update the group node in the graph with the calculated dimensions and center position
      groupNode.width = finalWidth;
      groupNode.height = finalHeight;
      groupNode.position = { x: groupX, y: groupY };
      groupNode.x = groupX;
      groupNode.y = groupY;
      g.setNode(groupNode.id, {
        ...groupNode,
      });
    }
  });

  // Use ELK rectpacking to position group nodes in a grid layout
  const elkGraph = {
    id: "root",
    layoutOptions: {
      algorithm: "rectpacking",
      aspectRatio: "1.5",
      "spacing.nodeNode": DEFAULT_GROUP_SPACING.toString(),
    },
    children: groupNodes.map((groupNode: Label) => ({
      id: groupNode.id,
      width: groupNode.width ?? 200,
      height: groupNode.height ?? 200,
    })),
  };

  const layoutedGraph = await elk.layout(elkGraph);

  // Create a map of group positions from ELK layout
  const groupPositions: Record<string, { x: number; y: number }> = {};
  layoutedGraph.children?.forEach((child) => {
    groupPositions[child.id] = {
      x: child.x ?? 0,
      y: child.y ?? 0,
    };
  });

  return {
    nodes: nodes.map((node: Label): Node => {
      const nodeWithPosition = g.node(node.id);
      const nodeX = nodeWithPosition?.x ?? 0;
      const nodeY = nodeWithPosition?.y ?? 0;

      if (node.parentId) {
        const parentNodeWithPosition = g.node(node.parentId);
        // Parent may not be in the graph (e.g. parentId is productTreeId, not a layout node)
        if (parentNodeWithPosition != null) {
          node.position = {
            x: nodeX - (parentNodeWithPosition.x ?? 0),
            y: nodeY - (parentNodeWithPosition.y ?? 0),
          };
        } else {
          node.position = { x: nodeX, y: nodeY };
        }
      } else {
        // Use ELK positions for group nodes
        const elkPosition = groupPositions[node.id];
        node.position = {
          x: elkPosition?.x ?? nodeX,
          y: elkPosition?.y ?? nodeY,
        };
      }

      return {
        ...node,
        id: node.id,
        position: node.position,
        data: { ...node.data, direction: options.direction },
      };
    }),
    edges,
  };
};
